<script setup lang="ts">
/**
 * Install.vue — 安装与上手（分端 tab 单页）
 * 路由: /install
 *
 * 分端结构（对照公众号安装指南文章）：
 *   电脑端：Chrome（商店/手动两个方式）/ Edge / Firefox / 其他 Chromium
 *   手机端（Android / HarmonyOS）：Edge 手机版 / Firefox 手机版（含「先打开任意网页」特殊提示与 APK 链接）
 *   iOS：不支持说明卡
 * 每个端一步到底：安装 → 固定 → 进入管理控制台 → 观赛，收尾统一「友情提示」。
 * 配图为可选增强：public/screenshots/install/<tab>/step-N.webp，缺图静默隐藏（StepImage）。
 */
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import BackLink from '@/components/BackLink.vue'
import PlatformIcon from '@/components/PlatformIcon.vue'
import YfbIcon from '@/components/YfbIcon.vue'
import StepImage from '@/components/StepImage.vue'
import { STORE_URLS, SUPPORT_EMAIL, type BrowserPlatform } from '@/config/brand'
import { useSeo } from '@/composables/useSeo'
import { detectBrowserInfo } from '@/utils/browserDetect'
import type { IconName } from '@/types/icons'

const { t, tm } = useI18n()
const router = useRouter()

useSeo('install')

type TabKey = 'chrome' | 'edge' | 'firefox' | 'chromium' | 'edgeMobile' | 'firefoxMobile'
type ChromeMethod = 'store' | 'manual'

const activeTab = ref<TabKey>('chrome')
const chromeMethod = ref<ChromeMethod>('store')

// UA 自动选中推荐 tab（与首页 Hero CTA 同一套检测，见 utils/browserDetect.ts）：
// 移动 Edge（EdgA/，含鸿蒙）→ edgeMobile、移动 Firefox → firefoxMobile；
// 桌面 edge/firefox → 对应 tab，其他国产 Chromium → chromium（手动 .crx）；
// iOS 保持默认 chrome tab，由页底 iOS 说明卡解释「暂不支持」。
onMounted(() => {
  const { platform, device } = detectBrowserInfo()
  if (device === 'ios') return
  if (device === 'mobile') {
    if (platform === 'edge') activeTab.value = 'edgeMobile'
    else if (platform === 'firefox') activeTab.value = 'firefoxMobile'
    return
  }
  if (platform === 'edge') activeTab.value = 'edge'
  else if (platform === 'firefox') activeTab.value = 'firefox'
  else if (platform === 'other') activeTab.value = 'chromium'
})

interface TabDef {
  key: TabKey
  icon: 'chrome' | 'edge' | 'firefox' | null
  fallbackIcon: IconName | null
}

const tabDefs: { group: 'desktop' | 'mobile'; tabs: TabDef[] }[] = [
  {
    group: 'desktop',
    tabs: [
      { key: 'chrome', icon: 'chrome', fallbackIcon: null },
      { key: 'edge', icon: 'edge', fallbackIcon: null },
      { key: 'firefox', icon: 'firefox', fallbackIcon: null },
      { key: 'chromium', icon: null, fallbackIcon: 'lucide:layers' }
    ]
  },
  {
    group: 'mobile',
    tabs: [
      { key: 'edgeMobile', icon: 'edge', fallbackIcon: null },
      { key: 'firefoxMobile', icon: 'firefox', fallbackIcon: null }
    ]
  }
]

// 配图约定：每个 tab 有图的关键步骤序号（1 起）；图放在
// public/screenshots/install/<tab>/step-N.webp，缺图由 StepImage 静默隐藏。
const IMG_STEPS: Record<TabKey | 'chromeManual', number[]> = {
  chrome: [2, 3, 4, 7],
  chromeManual: [1, 4, 5],
  edge: [2, 3, 4],
  firefox: [2, 3, 4],
  chromium: [],
  edgeMobile: [3, 4, 6, 7],
  firefoxMobile: [4, 5, 7, 8]
}

const stepsList = computed<string[]>(() => {
  if (activeTab.value === 'chrome') {
    const key = chromeMethod.value === 'store' ? 'chrome' : 'chromeManual'
    const r = tm(`install.tabs.steps.${key}`) as any
    return (r?.value ?? r ?? []) as string[]
  }
  const r = tm(`install.tabs.steps.${activeTab.value}`) as any
  return (r?.value ?? r ?? []) as string[]
})

const imgSteps = computed<number[]>(() => {
  if (activeTab.value === 'chrome') {
    return IMG_STEPS[chromeMethod.value === 'store' ? 'chrome' : 'chromeManual']
  }
  return IMG_STEPS[activeTab.value]
})

const IMG_BASE_MAP: Record<TabKey | 'chromeManual', string> = {
  chrome: '/screenshots/install/chrome',
  chromeManual: '/screenshots/install/chrome-manual',
  edge: '/screenshots/install/edge',
  firefox: '/screenshots/install/firefox',
  chromium: '',
  edgeMobile: '/screenshots/install/edge-mobile',
  firefoxMobile: '/screenshots/install/firefox-mobile'
}

const imgBase = computed(() => {
  if (activeTab.value === 'chrome') {
    return IMG_BASE_MAP[chromeMethod.value === 'store' ? 'chrome' : 'chromeManual']
  }
  return IMG_BASE_MAP[activeTab.value]
})

const introText = computed(() => {
  if (activeTab.value === 'chrome') {
    return t(`install.tabs.intro.${chromeMethod.value === 'store' ? 'chrome' : 'chromeManual'}`)
  }
  return t(`install.tabs.intro.${activeTab.value}`)
})

const isChromiumTab = computed(() => activeTab.value === 'chromium')

const storePlatform = computed<BrowserPlatform | null>(() => {
  if (activeTab.value === 'chrome' && chromeMethod.value === 'manual') return null
  if (activeTab.value === 'chrome') return 'chrome'
  if (activeTab.value === 'edge') return 'edge'
  if (activeTab.value === 'firefox') return 'firefox'
  return null
})

function goStore(browser: BrowserPlatform) {
  window.open(STORE_URLS[browser], '_blank', 'noopener,noreferrer')
}

function goManual() {
  router.push('/install/manual')
}

function tabLabel(key: TabKey): string {
  return t(`install.tabs.tabLabels.${key}`)
}
</script>

<template>
  <article class="install">
    <!-- 面包屑 -->
    <nav class="install__crumbs" :aria-label="t('install.crumbs')">
      <BackLink :label="t('install.crumbsHome')" to="/" />
      <span class="install__crumb-sep" aria-hidden="true">/</span>
      <span class="install__crumb install__crumb--current">
        {{ t('install.tabs.title') }}
      </span>
    </nav>

    <!-- 页面 H1 -->
    <header class="install__header">
      <h1 class="install__title">{{ t('install.tabs.title') }}</h1>
      <p class="install__subtitle">{{ t('install.tabs.subtitle') }}</p>
    </header>

    <!-- 顶部 callout（推荐桌面端） -->
    <aside class="install__callout" role="note">
      <span class="install__callout-badge">{{ t('install.tabs.calloutBadge') }}</span>
      <p class="install__callout-text">{{ t('install.tabs.calloutTitle') }}</p>
    </aside>

    <!-- 分端 tab -->
    <div class="install__tabs" role="tablist" :aria-label="t('install.tabs.title')">
      <template v-for="group in tabDefs" :key="group.group">
        <span class="install__group-label">{{ t(`install.tabs.groups.${group.group}`) }}</span>
        <button
          v-for="tab in group.tabs"
          :key="tab.key"
          type="button"
          role="tab"
          class="install__tab"
          :class="{ 'install__tab--active': activeTab === tab.key }"
          :aria-selected="activeTab === tab.key"
          @click="activeTab = tab.key"
        >
          <PlatformIcon v-if="tab.icon" :platform="tab.icon" :size="20" />
          <YfbIcon v-else-if="tab.fallbackIcon" :name="tab.fallbackIcon" :size="20" />
          <span>{{ tabLabel(tab.key) }}</span>
        </button>
      </template>
    </div>

    <!-- 当前 tab 内容 -->
    <section class="install__panel">
      <p class="install__intro">{{ introText }}</p>

      <!-- 商店一键 CTA（仅商店方式） -->
      <div v-if="storePlatform" class="install__store-cta">
        <button
          type="button"
          class="install__store-btn yfb-press"
          @click="goStore(storePlatform)"
        >
          <span>{{ t('install.store.detectedCta', { browser: tabLabel(storePlatform) }) }}</span>
          <YfbIcon name="lucide:external-link" :size="14" aria-hidden="true" />
        </button>
      </div>

      <!-- Chrome 方式切换：商店安装 / 手动安装 -->
      <div v-if="activeTab === 'chrome'" class="install__methods" role="tablist" :aria-label="t('install.tabs.methodBadges.store')">
        <button
          type="button"
          class="install__method"
          :class="{ 'install__method--active': chromeMethod === 'store' }"
          :aria-selected="chromeMethod === 'store'"
          @click="chromeMethod = 'store'"
        >
          {{ t('install.tabs.methodBadges.store') }}
        </button>
        <button
          type="button"
          class="install__method"
          :class="{ 'install__method--active': chromeMethod === 'manual' }"
          :aria-selected="chromeMethod === 'manual'"
          @click="chromeMethod = 'manual'"
        >
          {{ t('install.tabs.methodBadges.manual') }}
        </button>
      </div>

      <!-- 其他 Chromium：手动指南入口卡 -->
      <div v-if="isChromiumTab" class="install__link-card">
        <div class="install__link-text">
          <span class="install__link-title">{{ t('install.tabs.links.manualGuideText') }}</span>
          <span class="install__link-hint">{{ t('install.tabs.links.manualGuideHint') }}</span>
        </div>
        <button type="button" class="install__link-btn yfb-press" @click="goManual">
          {{ t('install.tabs.chromiumCta') }} →
        </button>
      </div>

      <!-- 步骤 -->
      <ol v-if="stepsList.length" class="install__steps">
        <li
          v-for="(step, idx) in stepsList"
          :key="idx"
          class="install__step"
        >
          <span class="install__step-num">{{ String(idx + 1).padStart(2, '0') }}</span>
          <div class="install__step-body">
            <span class="install__step-text">{{ step }}</span>
            <StepImage
              v-if="imgSteps.includes(idx + 1)"
              :base-path="imgBase"
              :step="idx + 1"
              :alt="step"
              class="install__step-img"
            />
          </div>
        </li>
      </ol>

      <!-- chromeManual：手动指南入口 -->
      <div v-if="activeTab === 'chrome' && chromeMethod === 'manual'" class="install__link-card">
        <div class="install__link-text">
          <span class="install__link-title">{{ t('install.tabs.links.manualGuideText') }}</span>
          <span class="install__link-hint">{{ t('install.tabs.links.manualGuideHint') }}</span>
        </div>
        <button type="button" class="install__link-btn yfb-press" @click="goManual">
          {{ t('install.tabs.chromiumCta') }} →
        </button>
      </div>

      <!-- firefoxMobile：APK 链接 -->
      <div v-if="activeTab === 'firefoxMobile'" class="install__link-card">
        <div class="install__link-text">
          <span class="install__link-title">{{ t('install.tabs.links.apkText') }}</span>
        </div>
        <a
          :href="t('install.tabs.links.apkHref')"
          class="install__link-btn yfb-press"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ t('download.directDownload') }} ↗
        </a>
      </div>

      <!-- 完成行 + 友情提示 -->
      <template v-if="stepsList.length">
        <p class="install__done">
          <YfbIcon name="ph:check-circle-bold" :size="16" />
          <span>{{ t('install.tabs.doneText') }}</span>
        </p>
        <aside class="install__notice" role="note">
          <span class="install__notice-badge">{{ t('install.tabs.noticeTitle') }}</span>
          <p class="install__notice-text">{{ t('install.tabs.notice') }}</p>
        </aside>
      </template>
    </section>

    <!-- iOS 不支持说明卡 -->
    <section class="install__ios" aria-label="iOS / iPadOS">
      <header class="install__ios-head">
        <YfbIcon name="lucide:apple" :size="18" />
        <h2 class="install__ios-title">{{ t('install.tabs.ios.title') }}</h2>
      </header>
      <p class="install__ios-reason">{{ t('install.tabs.ios.reason') }}</p>
      <p class="install__ios-note">{{ t('install.tabs.ios.note') }}</p>
    </section>

    <!-- 底部支持 -->
    <footer class="install__help">
      <p class="install__help-text">
        {{ t('install.tabs.helpLine', { email: SUPPORT_EMAIL }) }}
      </p>
    </footer>
  </article>
</template>

<style scoped>
.install {
  max-width: 1024px;
  margin: 0 auto;
  padding: var(--yfb-sp-7) var(--yfb-sp-6) var(--yfb-sp-9);
}

/* ===== 面包屑 ===== */
.install__crumbs {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  margin-bottom: 24px;
  font-size: var(--yfb-fs-sm);
  font-family: var(--yfb-mono);
}

.install__crumb {
  padding: var(--yfb-sp-1) var(--yfb-sp-2);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  color: var(--yfb-ink);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
}

.install__crumb--current {
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  cursor: default;
}

.install__crumb-sep {
  color: var(--yfb-rule-soft);
  font-weight: 700;
}

/* ===== H1 ===== */
.install__header {
  padding-bottom: 32px;
  margin-bottom: 24px;
  border-bottom: 1px solid var(--yfb-rule);
}

.install__title {
  font-family: var(--yfb-font-heading);
  font-size: clamp(1.75rem, 4vw, var(--yfb-fs-3xl));
  font-weight: 700;
  margin: 0 0 12px;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-display-tight);
  text-wrap: balance;
}

.install__subtitle {
  font-size: var(--yfb-fs-lg);
  color: var(--yfb-text-secondary);
  margin: 0;
  line-height: var(--yfb-lh-body);
}

/* ===== 顶部 callout ===== */
.install__callout {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-4);
  padding: var(--yfb-sp-4) var(--yfb-sp-5);
  margin-bottom: 32px;
  background-color: var(--yfb-surface-2);
  color: var(--yfb-ink);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-lg);
  box-shadow: var(--yfb-shadow-2);
}

.install__callout-badge {
  flex-shrink: 0;
  padding: var(--yfb-sp-1) var(--yfb-sp-2);
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  font-family: var(--yfb-mono);
  font-size: var(--yfb-fs-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-eyebrow);
  border-radius: var(--yfb-radius-sm);
}

.install__callout-text {
  margin: 0;
  font-size: var(--yfb-fs-md);
  font-weight: 700;
  line-height: var(--yfb-lh-body);
}

/* ===== Tab 条 ===== */
.install__tabs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--yfb-sp-2);
  margin-bottom: var(--yfb-sp-5);
}

.install__group-label {
  font-family: var(--yfb-font-heading);
  font-size: var(--yfb-fs-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  color: var(--yfb-accent-2);
  margin: 0 var(--yfb-sp-1) 0 var(--yfb-sp-3);
}

.install__group-label:first-child {
  margin-left: 0;
}

.install__tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: var(--yfb-sp-2) var(--yfb-sp-4);
  background-color: var(--yfb-surface);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
  color: var(--yfb-text-secondary);
  font-family: inherit;
  font-size: var(--yfb-fs-sm);
  font-weight: 700;
  cursor: pointer;
  transition: border-color var(--yfb-anim-fast) ease-out, color var(--yfb-anim-fast) ease-out, box-shadow var(--yfb-anim-fast) ease-out;
}

.install__tab:hover {
  border-color: var(--yfb-accent);
  color: var(--yfb-accent);
}

.install__tab--active {
  background-color: var(--yfb-accent);
  border-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  box-shadow: 0 0 12px var(--yfb-glow-soft);
}

/* ===== 面板 ===== */
.install__panel {
  padding: var(--yfb-sp-6);
  background-color: var(--yfb-surface);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-lg);
  box-shadow: var(--yfb-shadow-2);
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-5);
}

.install__intro {
  margin: 0;
  font-size: var(--yfb-fs-md);
  line-height: var(--yfb-lh-body);
  color: var(--yfb-text-secondary);
  max-width: 70ch;
}

/* 商店 CTA */
.install__store-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  padding: var(--yfb-sp-3) var(--yfb-sp-5);
  background-image: var(--yfb-grad-cta);
  color: var(--yfb-on-accent);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
  box-shadow: 0 0 14px var(--yfb-glow);
  font-family: inherit;
  font-size: var(--yfb-fs-md);
  font-weight: 700;
  letter-spacing: var(--yfb-ls-button);
  cursor: pointer;
  transition: filter var(--yfb-anim-fast) ease-out, transform var(--yfb-anim-fast) ease-out;
}

.install__store-btn:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
}

/* 方式切换 */
.install__methods {
  display: inline-flex;
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
  overflow: hidden;
  align-self: flex-start;
}

.install__method {
  padding: var(--yfb-sp-2) var(--yfb-sp-4);
  background-color: var(--yfb-surface);
  border: none;
  color: var(--yfb-text-secondary);
  font-family: inherit;
  font-size: var(--yfb-fs-sm);
  font-weight: 700;
  cursor: pointer;
  transition: background-color var(--yfb-anim-fast) ease-out, color var(--yfb-anim-fast) ease-out;
}

.install__method + .install__method {
  border-left: 1px solid var(--yfb-rule);
}

.install__method--active {
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
}

/* 链接卡 */
.install__link-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--yfb-sp-4);
  padding: var(--yfb-sp-4) var(--yfb-sp-5);
  background-color: var(--yfb-surface-2);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
}

.install__link-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.install__link-title {
  font-size: var(--yfb-fs-sm);
  font-weight: 700;
  color: var(--yfb-ink);
}

.install__link-hint {
  font-size: var(--yfb-fs-xs);
  color: var(--yfb-text-secondary);
  line-height: 1.4;
}

.install__link-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  padding: var(--yfb-sp-2) var(--yfb-sp-4);
  background-color: var(--yfb-surface);
  color: var(--yfb-accent);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
  font-family: inherit;
  font-size: var(--yfb-fs-sm);
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color var(--yfb-anim-fast) ease-out, box-shadow var(--yfb-anim-fast) ease-out;
}

.install__link-btn:hover {
  border-color: var(--yfb-accent);
  box-shadow: 0 0 14px var(--yfb-glow);
}

/* 步骤 */
.install__steps {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-3);
}

.install__step {
  display: flex;
  align-items: flex-start;
  gap: var(--yfb-sp-4);
  padding: var(--yfb-sp-4);
  background-color: var(--yfb-surface-2);
  border: 1px solid var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-md);
  transition: border-color var(--yfb-anim-fast) ease-out;
}

.install__step:hover {
  border-color: var(--yfb-accent);
}

.install__step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 28px;
  padding: 0 8px;
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  font-family: var(--yfb-mono);
  font-weight: 700;
  font-size: var(--yfb-fs-sm);
  border-radius: var(--yfb-radius-sm);
  flex-shrink: 0;
}

.install__step-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-2);
  min-width: 0;
}

.install__step-text {
  font-size: var(--yfb-fs-base);
  line-height: var(--yfb-lh-lead);
  color: var(--yfb-ink);
}

/* 完成行 */
.install__done {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  margin: 0;
  font-size: var(--yfb-fs-sm);
  font-weight: 700;
  color: var(--yfb-signal-ok);
}

/* 友情提示（全边框 callout） */
.install__notice {
  display: flex;
  align-items: flex-start;
  gap: var(--yfb-sp-3);
  padding: var(--yfb-sp-4) var(--yfb-sp-5);
  background-color: var(--yfb-surface-2);
  border: 1px solid var(--yfb-signal-warning);
  border-radius: var(--yfb-radius-md);
}

.install__notice-badge {
  flex-shrink: 0;
  padding: 2px 8px;
  background-color: var(--yfb-signal-warning);
  color: var(--yfb-on-warning);
  font-family: var(--yfb-mono);
  font-size: var(--yfb-fs-xs);
  font-weight: 700;
  border-radius: var(--yfb-radius-sm);
}

.install__notice-text {
  margin: 0;
  font-size: var(--yfb-fs-sm);
  line-height: var(--yfb-lh-body);
  color: var(--yfb-ink);
}

/* iOS 卡 */
.install__ios {
  margin-top: var(--yfb-sp-7);
  padding: var(--yfb-sp-5) var(--yfb-sp-6);
  background-color: var(--yfb-surface-2);
  border: 1px dashed var(--yfb-rule);
  border-radius: var(--yfb-radius-lg);
}

.install__ios-head {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  margin-bottom: var(--yfb-sp-3);
  color: var(--yfb-ink);
}

.install__ios-title {
  font-family: var(--yfb-font-heading);
  font-size: var(--yfb-fs-lg);
  font-weight: 700;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-display);
}

.install__ios-reason {
  margin: 0 0 6px;
  font-size: var(--yfb-fs-sm);
  font-weight: 700;
  color: var(--yfb-signal-danger);
}

.install__ios-note {
  margin: 0;
  font-size: var(--yfb-fs-sm);
  color: var(--yfb-text-secondary);
  line-height: var(--yfb-lh-body);
}

/* ===== 底部支持 ===== */
.install__help {
  margin-top: 48px;
  padding-top: 32px;
  border-top: 1px solid var(--yfb-rule);
  text-align: center;
}

.install__help-text {
  margin: 0;
  font-size: var(--yfb-fs-sm);
  color: var(--yfb-text-secondary);
  line-height: var(--yfb-lh-body);
}

/* ===== 移动端 ===== */
@media (max-width: 767px) {
  .install {
    padding: var(--yfb-sp-6) var(--yfb-sp-4) var(--yfb-sp-9);
  }

  .install__group-label {
    width: 100%;
    margin-left: 0;
  }

  .install__tab {
    padding: var(--yfb-sp-2) var(--yfb-sp-3);
    font-size: var(--yfb-fs-xs);
  }

  .install__panel {
    padding: var(--yfb-sp-4);
  }

  .install__link-card {
    flex-direction: column;
    align-items: stretch;
  }

  .install__link-btn {
    justify-content: center;
  }

  .install__step {
    padding: var(--yfb-sp-3);
  }
}

@media (prefers-reduced-motion: reduce) {
  .install__tab,
  .install__store-btn,
  .install__method,
  .install__link-btn,
  .install__step {
    transition: none;
  }
}
</style>
